import type { ListResponse } from 'Services/SalesManagement/types'
import type { LoadingStates } from 'Store'
import {
  createAxiosAsyncThunk,
  createAxiosAsyncThunkWithParams,
  getAxiosInstance,
  type ValidationErrors
} from 'Util/ApiClient'
import actionGenerator from 'Util/actionGenerator'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios, { type AxiosError } from 'axios'

export enum DeviceType {
  walttiKiosk = '1',
  walttiSelfServiceCheckout = '2',
  foliKiosk = '3',
  foliSelfServiceCheckout = '4'
}

export type DeviceTypes =
  | DeviceType.walttiKiosk
  | DeviceType.walttiSelfServiceCheckout
  | DeviceType.foliKiosk
  | DeviceType.foliSelfServiceCheckout

export interface DeviceConfigShape {
  type_configuration: number
  machine_id: string
  name: string
}

export interface DeviceConfigDetailShape {
  machine_id: string
  name: string
  type_configuration: number
  device_fields: Record<string, unknown>
}

export interface DeviceParamShape {
  page: number
  search: string
  type_configuration: string
}

export interface DeviceTypeShape {
  type: number
  common_fields: Record<string, never>
  masked_common_fields: Record<string, never>
  full_schema: { required: [] }
}

type FetchDeviceConfigListParams = DeviceParamShape

export interface WalttiPosRemoteConfigState {
  devices: DeviceConfigShape[]
  count: number
  next: string | null
  previous: string | null
  loading: LoadingStates
  singleDevice: DeviceConfigDetailShape | null
  singleDeviceLoading: LoadingStates
  deviceTypesLoading: LoadingStates
  deviceTypeOptionsLoading: LoadingStates
  submitLoading: LoadingStates
  deleteLoading: LoadingStates
  deviceTypes: DeviceTypeShape[]
  deviceTypeOptions: DeviceTypeOptions
}

const initialState: WalttiPosRemoteConfigState = {
  devices: [],
  count: 0,
  next: null,
  previous: null,
  loading: 'idle',
  singleDevice: null,
  singleDeviceLoading: 'idle',
  deviceTypesLoading: 'idle',
  deviceTypeOptionsLoading: 'idle',
  submitLoading: 'idle',
  deleteLoading: 'idle',
  deviceTypes: [],
  deviceTypeOptions: {
    type: {}
  }
}

const fetchDeviceConfigList = createAxiosAsyncThunkWithParams<
  ListResponse<DeviceConfigShape>,
  FetchDeviceConfigListParams | undefined
>('walttiPosRemoteConfig/fetchDeviceList', '/api/v1/device-configuration/')

interface FetchSingleDeviceConfigRequest {
  machine_id: string
}

type FetchSingleDeviceConfigResponse = DeviceConfigDetailShape

const fetchSingleDeviceConfig = createAsyncThunk<
  FetchSingleDeviceConfigResponse,
  FetchSingleDeviceConfigRequest,
  {
    rejectValue: ValidationErrors
  }
>('walttiPosRemoteConfig/fetchSingleDevice', async (data, { rejectWithValue }) => {
  try {
    const { machine_id } = data
    const response = await getAxiosInstance().get<FetchSingleDeviceConfigResponse>(
      `/api/v1/device-configuration/${machine_id}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type FetchDeviceTypesResponse = DeviceTypeShape[]

const fetchDeviceTypes = createAxiosAsyncThunk<FetchDeviceTypesResponse, undefined>(
  'walttiPosRemoteConfig/fetchDeviceTypes',
  '/api/v1/device-types/'
)

type DeviceTypeOptionShape = { [key: string]: string }

export interface DeviceTypeOptions {
  type: DeviceTypeOptionShape
}

interface FetchDeviceTypeOptions {
  field_choices: DeviceTypeOptions
}

const fetchDeviceTypeOptions = createAxiosAsyncThunk<FetchDeviceTypeOptions, undefined>(
  'walttiPosRemoteConfig/fetchDeviceTypeOptions',
  '/api/v1/device-types/',
  'OPTIONS'
)

type CreateDeviceConfigRequest = DeviceConfigDetailShape

type CreateDeviceConfigResponse = DeviceConfigDetailShape

const createDeviceConfig = createAsyncThunk<
  CreateDeviceConfigResponse,
  CreateDeviceConfigRequest,
  {
    rejectValue: ValidationErrors
  }
>('walttiPosRemoteConfig/createDeviceConfig', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().post<CreateDeviceConfigResponse>(
      `/api/v1/device-configuration/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type EditDeviceConfigRequest = DeviceConfigDetailShape

type EditDeviceConfigResponse = DeviceConfigDetailShape

const editDeviceConfig = createAsyncThunk<
  EditDeviceConfigResponse,
  EditDeviceConfigRequest,
  {
    rejectValue: ValidationErrors
  }
>('walttiPosRemoteConfig/editDeviceConfig', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().put<EditDeviceConfigResponse>(
      `/api/v1/device-configuration/${fields.machine_id}/`,
      fields
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

type DeleteDeviceConfigResponse = Record<string, never>

export interface DeleteDeviceConfigRequest {
  machine_id: string
}

const deleteDeviceConfig = createAsyncThunk<
  DeleteDeviceConfigResponse,
  DeleteDeviceConfigRequest,
  {
    rejectValue: ValidationErrors
  }
>('walttiPosRemoteConfig/deleteDeviceConfig', async (data, { rejectWithValue }) => {
  try {
    const { ...fields } = data
    const response = await getAxiosInstance().delete<DeleteDeviceConfigResponse>(
      `/api/v1/device-configuration/${fields.machine_id}/`
    )
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const error: AxiosError<ValidationErrors> = err // cast the error for access
      if (!error.response) {
        throw err
      }
      return rejectWithValue({ ...error.response.data, status: error.response.status })
    }
    throw err
  }
})

const genericActions = actionGenerator('walttiPosRemoteConfig')

const walttiPosRemoteConfigSlice = createSlice({
  name: genericActions.name,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchDeviceConfigList.fulfilled, (state, { payload }) => {
        state.devices = payload.results
        state.count = payload.count
        state.next = payload.next
        state.previous = payload.previous
        state.loading = 'fulfilled'
      })
      .addCase(fetchDeviceConfigList.pending, state => {
        state.devices = initialState.devices
        state.count = initialState.count
        state.next = initialState.next
        state.previous = initialState.previous
        state.loading = 'pending'
        // reset singleDevice here so that it won't show previous device details when creating a new config
        state.singleDevice = initialState.singleDevice
      })
      .addCase(fetchDeviceConfigList.rejected, state => {
        state.loading = 'rejected'
      })

      .addCase(fetchSingleDeviceConfig.fulfilled, (state, { payload }) => {
        state.singleDevice = payload
        state.singleDeviceLoading = 'fulfilled'
      })
      .addCase(fetchSingleDeviceConfig.pending, state => {
        state.singleDevice = initialState.singleDevice
        state.singleDeviceLoading = 'pending'
      })
      .addCase(fetchSingleDeviceConfig.rejected, state => {
        state.singleDeviceLoading = 'rejected'
      })

      .addCase(createDeviceConfig.fulfilled, (state, { payload }) => {
        state.singleDevice = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(createDeviceConfig.pending, state => {
        state.singleDevice = initialState.singleDevice
        state.submitLoading = 'pending'
      })
      .addCase(createDeviceConfig.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(editDeviceConfig.fulfilled, (state, { payload }) => {
        state.singleDevice = payload
        state.submitLoading = 'fulfilled'
      })
      .addCase(editDeviceConfig.pending, state => {
        state.singleDevice = initialState.singleDevice
        state.submitLoading = 'pending'
      })
      .addCase(editDeviceConfig.rejected, state => {
        state.submitLoading = 'rejected'
      })

      .addCase(deleteDeviceConfig.fulfilled, state => {
        state.deleteLoading = 'fulfilled'
      })
      .addCase(deleteDeviceConfig.pending, state => {
        state.deleteLoading = 'pending'
      })
      .addCase(deleteDeviceConfig.rejected, state => {
        state.deleteLoading = 'rejected'
      })

      .addCase(fetchDeviceTypes.fulfilled, (state, { payload }) => {
        state.deviceTypes = payload
        state.deviceTypesLoading = 'fulfilled'
      })
      .addCase(fetchDeviceTypes.pending, state => {
        state.deviceTypes = initialState.deviceTypes
        state.deviceTypesLoading = 'pending'
      })
      .addCase(fetchDeviceTypes.rejected, state => {
        state.deviceTypesLoading = 'rejected'
      })

      .addCase(fetchDeviceTypeOptions.fulfilled, (state, { payload }) => {
        state.deviceTypeOptions = payload.field_choices
        state.deviceTypeOptionsLoading = 'fulfilled'
      })
      .addCase(fetchDeviceTypeOptions.pending, state => {
        state.deviceTypeOptions = initialState.deviceTypeOptions
        state.deviceTypeOptionsLoading = 'pending'
      })
      .addCase(fetchDeviceTypeOptions.rejected, state => {
        state.deviceTypeOptionsLoading = 'rejected'
      })

      .addCase(genericActions.ResetAction, () => initialState)
  }
})

export default walttiPosRemoteConfigSlice.reducer

export {
  createDeviceConfig,
  deleteDeviceConfig,
  editDeviceConfig,
  fetchDeviceConfigList,
  fetchDeviceTypeOptions,
  fetchDeviceTypes,
  fetchSingleDeviceConfig
}
